import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lightbulb, FileText, Layers, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TicketDialog } from "@/components/agyin/RaiseTicketButton";
import { AnalyticsCard, KbProposeCard } from "@/components/agyin/ProExtras";

const box = "mt-2 rounded-lg border p-3 text-xs";
const log = (action, detail) => api.post("/agyin/log", { action, detail }).catch(() => {});
const TITLES = { po_draft: "Draft purchase order", invoice_draft: "Draft invoice", customer_comms_draft: "Draft customer message", job_convert: "Convert quote to job" };

export function Insight({ a, onClose }) {
  const navigate = useNavigate();
  const admin = useAuth().user?.role === "tenant_admin";
  const [ticket, setTicket] = useState(null);
  const openTicket = (i) => { log("insight_ticket_opened", i); setTicket({ summary: i.title + (i.work_center ? ` (${i.work_center})` : ""), description: `${i.detail}\n\nRaised from an Agyin Pro insight.`, category: "data_issue", severity: i.kind === "overdue_subscription" ? "high" : "medium" }); };
  return (
    <div className="mt-2 space-y-2" data-testid="agyin-insight-card">
      {a.items.map((i, k) => <div key={k} className={`${box} po-tone-warning`} data-testid={`agyin-insight-${k}`}>
        <div className="flex items-center gap-1.5 font-semibold" data-testid={`agyin-insight-title-${k}`}><Lightbulb className="h-3.5 w-3.5" />{i.title}</div><div data-testid={`agyin-insight-detail-${k}`}>{i.detail}</div>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {(i.next || i.route) && <Button size="sm" variant="outline" data-testid={`agyin-insight-next-${k}`} onClick={() => { onClose(); log("insight_confirmed", i); navigate(i.next?.route || i.route); }}>{i.next?.label || "Open"}<ArrowRight className="ml-1 h-3 w-3" /></Button>}
          {admin && <Button size="sm" variant="outline" data-testid={`agyin-insight-ticket-${k}`} onClick={() => openTicket(i)}><LifeBuoy className="mr-1 h-3 w-3" />Create ticket</Button>}
        </div>
      </div>)}
      {ticket && <TicketDialog open initial={ticket} context={{ route: "/app", module: "agyin_insight" }} onClose={() => setTicket(null)} />}
    </div>);
}

async function execute(a) {
  const d = a.draft || {};
  if (a.action === "po_draft") {
    if (!d.vendor_id) throw new Error("No matching supplier. Create the supplier first.");
    return api.post("/proc/pos", { vendor_id: d.vendor_id, notes: d.email, lines: (d.lines || []).map((l) => ({ item_id: l.item_id || null, description: l.description || l.item_code, quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0 })) });
  }
  if (a.action === "invoice_draft") {
    if (!d.customer_id) throw new Error("No matching customer.");
    const due = new Date(Date.now() + (d.payment_terms_days || 30) * 864e5).toISOString().slice(0, 10);
    return api.post("/tenant/invoices", { customer_id: d.customer_id, due_date: due, job_ids: d.job_id ? [d.job_id] : [], line_items: (d.lines || []).map((l) => ({ description: l.description, quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0 })) });
  }
  if (a.action === "customer_comms_draft") {
    if (!d.customer_id) throw new Error("No matching customer.");
    return api.post("/agyin/comms/send", { customer_id: d.customer_id, subject: d.subject, body: d.body });
  }
  if (!a.quote?.id) throw new Error("Quote not found.");
  return api.post(`/quotes/${a.quote.id}/convert`);
}

function Draft({ a }) {
  const [state, setState] = useState("draft");
  const d = a.draft || {};
  const go = async () => {
    try { await execute(a); setState("done"); log(`${a.action}_confirmed`, d); toast.success(a.action === "customer_comms_draft" ? "Message sent" : "Saved"); }
    catch (e) { toast.error(e.response ? errMsg(e) : e.message); }
  };
  return (
    <div className={box} style={{ borderColor: "var(--po-border)" }} data-testid={`agyin-${a.action}-card`}>
      <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-900"><FileText className="h-3.5 w-3.5" />{TITLES[a.action]} · {state === "done" ? "saved" : "nothing saved yet"}</div>
      {a.action === "job_convert" ? <div>Quote {a.quote?.number || a.quote_number} {a.quote ? `(${a.quote.status})` : <StatusBadge tone="warning" label="not found" />}</div> : <>
        {(d.supplier || d.customer_name) && <div>{d.supplier ? "Supplier" : "Customer"}: <b>{d.supplier || d.customer_name}</b>{!(d.vendor_id || d.customer_id) && <StatusBadge tone="warning" label="no match" className="ml-1" />}</div>}
        {d.subject && <div className="mt-1 font-medium">{d.subject}</div>}
        {d.body && <p className="mt-1 whitespace-pre-wrap text-slate-600">{d.body}</p>}
        <ul className="mt-1 list-disc pl-4">{(d.lines || []).map((l, i) => <li key={i}>{l.description || l.item_code} · qty {l.quantity}{l.unit_price ? ` · ${l.unit_price}` : ""}</li>)}</ul>
        {d.email && <p className="mt-1 whitespace-pre-wrap text-slate-500" data-testid="agyin-po-email">{d.email}</p>}</>}
      {state === "draft" && <div className="mt-2 flex gap-2"><Button size="sm" data-testid={`agyin-${a.action}-confirm`} onClick={go} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white">{a.action === "customer_comms_draft" ? "Confirm & send" : "Confirm & save"}</Button>
        <Button size="sm" variant="outline" data-testid={`agyin-${a.action}-dismiss`} onClick={() => { setState("dismissed"); log(`${a.action}_dismissed`); }}>Dismiss</Button></div>}
    </div>);
}

function Bulk({ a }) {
  const [pv, setPv] = useState(null);
  const [done, setDone] = useState(null);
  const preview = async () => { try { setPv((await api.post("/agyin/bulk/preview", { op: a.op, params: a.params })).data); } catch (e) { toast.error(errMsg(e)); } };
  const run = async () => { try { const r = (await api.post("/agyin/bulk/execute", { preview_id: pv.preview_id, confirm: true })).data; setDone(r); log("bulk_action_confirmed", r); toast.success(`${r.affected} record(s) done`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={box} style={{ borderColor: "var(--po-border)" }} data-testid="agyin-bulk-card">
      <div className="flex items-center gap-1.5 font-semibold text-slate-900"><Layers className="h-3.5 w-3.5" />Bulk action · {a.label}</div>
      {!pv && <Button size="sm" variant="outline" className="mt-2" data-testid="agyin-bulk-preview" onClick={preview}>Preview affected records</Button>}
      {pv && <div className="mt-2" data-testid="agyin-bulk-preview-list"><div className="font-medium">{pv.count} record(s) will be affected</div>
        <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-4">{pv.records.map((r) => <li key={r.id}>{r.label}{r.amount != null ? ` · ${r.amount}` : ""}{r.steps ? ` · ${r.steps.length} open steps` : ""}</li>)}</ul>
        {!done && pv.count > 0 && <Button size="sm" className="mt-2 bg-[#13294B] hover:bg-[#1c3a66]" data-testid="agyin-bulk-confirm" onClick={run}>Confirm and run on {pv.count}</Button>}
        {done && <div className="mt-1 text-emerald-700" data-testid="agyin-bulk-done">Done: {done.affected} affected</div>}</div>}
    </div>);
}

export function ProCard({ a, onClose }) {
  if (a.action === "insight") return <Insight a={a} onClose={onClose} />;
  if (a.action === "bulk_action") return <Bulk a={a} />;
  if (a.action === "analytics_query") return <AnalyticsCard a={a} />;
  if (a.action === "kb_propose") return <KbProposeCard a={a} />;
  if (TITLES[a.action]) return <Draft a={a} />;
  return null;
}
